export interface TechnicalProfile {
  id: string;
  protocol: string;
}

export interface StepClaimChange {
  name: string;
  previousValue: string;
  value: string;
}

export interface OrchestrationStepSummary {
  step: string;
  startedAt: string;
  endedAt: string;
  durationMs: number | null;
  technicalProfiles: TechnicalProfile[];
  claimsUpdated: StepClaimChange[];
}

export interface ClaimsTransformationRow {
  item: string;
  name: string;
  value: string;
}

export interface ClaimIssue {
  type: "empty-not-initialized" | "not-null-mismatch" | "not-pair-mismatch";
  severity: "warning" | "error";
  message: string;
  relatedClaims?: string[];
}

export interface ClaimAnalysisRow {
  name: string;
  values: string[];
  issues: ClaimIssue[];
}

function getLatestValue(values: string[] | undefined): string {
  if (!values || values.length === 0) return "Null";
  return values[values.length - 1] ?? "Null";
}

function isNullLike(value: string | undefined): boolean {
  return value === undefined || value === null || value === "Null";
}

function isBooleanLike(value: string | undefined): boolean {
  return value === "True" || value === "False" || value === "Null";
}

function invertBooleanLike(value: string): string | null {
  if (value === "True") return "False";
  if (value === "False") return "True";
  return null;
}

function analyzeClaims(claims: Record<string, string[]>): ClaimAnalysisRow[] {
  const rows: ClaimAnalysisRow[] = Object.keys(claims)
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({
      name,
      values: claims[name],
      issues: []
    }));

  const rowMap = new Map(rows.map(row => [row.name, row]));

  for (const row of rows) {
    const latest = getLatestValue(row.values);

    // 1. empty is Null
    if (row.name === "empty" && isNullLike(latest)) {
      row.issues.push({
        type: "empty-not-initialized",
        severity: "warning",
        message:
          "Claim 'empty' is Null. Empty sentinel may not have been initialised."
      });
    }

    // 2. xNotNull = True while x = Null
    if (row.name.endsWith("NotNull")) {
      const baseName = row.name.slice(0, -"NotNull".length);
      const baseRow = rowMap.get(baseName);
      const baseLatest = getLatestValue(baseRow?.values);

      if (latest === "True" && isNullLike(baseLatest)) {
        row.issues.push({
          type: "not-null-mismatch",
          severity: "error",
          message: `'${row.name}' is True while '${baseName}' is Null.`,
          relatedClaims: [baseName]
        });

        baseRow?.issues.push({
          type: "not-null-mismatch",
          severity: "error",
          message: `'${baseName}' is Null while '${row.name}' is True.`,
          relatedClaims: [row.name]
        });
      }
    }

    // 3. xSomething vs xNotSomething mismatch
    const notMatch = row.name.match(/^(.+?)Not([A-Z].*)$/);
    if (notMatch) {
      const positiveName = `${notMatch[1]}${notMatch[2]}`;
      const positiveRow = rowMap.get(positiveName);

      if (positiveRow) {
        const positiveLatest = getLatestValue(positiveRow.values);

        if (isNullLike(latest) || isNullLike(positiveLatest)) {
          row.issues.push({
            type: "not-pair-mismatch",
            severity: "warning",
            message: `'${row.name}' / '${positiveName}' contains Null.`,
            relatedClaims: [positiveName]
          });
        } else if (isBooleanLike(latest) && isBooleanLike(positiveLatest)) {
          const expected = invertBooleanLike(positiveLatest);
          if (expected !== null && latest !== expected) {
            row.issues.push({
              type: "not-pair-mismatch",
              severity: "error",
              message: `'${row.name}' is ${latest} but expected ${expected} from '${positiveName}=${positiveLatest}'.`,
              relatedClaims: [positiveName]
            });

            positiveRow.issues.push({
              type: "not-pair-mismatch",
              severity: "error",
              message: `'${positiveName}' is ${positiveLatest} but '${row.name}' is not its opposite.`,
              relatedClaims: [row.name]
            });
          }
        }
      }
    }
  }

  return rows;
}

export interface ClaimsTransformationGroup {
  id: string;
  rows: ClaimsTransformationRow[];
}

export interface TokenInfo {
  label: string;
  contentType: string;
  value: string;
  jwtMsLink?: string;
  kind: "jwt" | "json" | "form" | "text";
  preview: string;
}

export interface TraceAnalysis {
  policyId: string;
  correlationId: string;
  eventInstance: string;
  orchestrationSteps: string;
  orchestrationStepSummaries: OrchestrationStepSummary[];
  journeyCompleted: boolean;
  technicalProfiles: TechnicalProfile[];
  validationTechnicalProfiles: string[];
  claimsTransformations: ClaimsTransformationGroup[];
  exceptions: string[];
  tokens: TokenInfo[];
  claims: ClaimAnalysisRow[];
  rawJson: string;
  timestamp: string;
}

type JsonValue = unknown;

export function looksLikeB2CTracePayload(text: string): boolean {
  const trimmed = text.trim();
  return (
    trimmed.startsWith('[\n  {\n    "Kind": "Headers"') ||
    trimmed.startsWith('[{"Kind":"Headers"') ||
    trimmed.startsWith('[\r\n  {\r\n    "Kind": "Headers"')
  );
}

function looksLikeJwt(value: string): boolean {
  return /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.[A-Za-z0-9-_.+/=]*$/i.test(value);
}

function looksLikeJson(value: string): boolean {
  const trimmed = value.trim();
  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  );
}

function looksLikeFormEncoded(value: string): boolean {
  return value.includes("=") && value.includes("&");
}

function buildPreview(value: string, maxLength = 300): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return `${trimmed.slice(0, maxLength)}…`;
}

function walkJson(node: JsonValue, visit: (n: any) => void): void {
  if (node === null || node === undefined) return;
  visit(node);

  if (Array.isArray(node)) {
    for (const child of node) walkJson(child, visit);
    return;
  }

  if (typeof node === "object") {
    for (const key of Object.keys(node as Record<string, unknown>)) {
      walkJson((node as Record<string, unknown>)[key], visit);
    }
  }
}

function collectValuesEntriesByKey(root: JsonValue, key: string): any[] {
  const out: any[] = [];

  walkJson(root, node => {
    if (!node || typeof node !== "object") return;

    const values = (node as any).Values;
    if (!Array.isArray(values)) return;

    for (const entry of values) {
      if (entry?.Key === key) out.push(entry);
    }
  });

  return out;
}

function collectObjectsWithProp(root: JsonValue, propName: string): any[] {
  const out: any[] = [];

  walkJson(root, node => {
    if (
      node &&
      typeof node === "object" &&
      !Array.isArray(node) &&
      Object.prototype.hasOwnProperty.call(node, propName)
    ) {
      out.push(node);
    }
  });

  return out;
}

function collectExceptions(root: JsonValue): string[] {
  const out: string[] = [];

  walkJson(root, node => {
    if (!node || typeof node !== "object") return;

    if (Object.prototype.hasOwnProperty.call(node, "Exception")) {
      const ex = (node as any).Exception;
      out.push(ex?.Message ?? String(ex));
    }

    const values = (node as any).Values;
    if (Array.isArray(values)) {
      for (const entry of values) {
        if (entry?.Key === "Exception") {
          out.push(entry?.Value?.Message ?? String(entry?.Value));
        }
      }
    }
  });

  return out;
}

function collectByPropName(root: JsonValue, propName: string): any[] {
  const out: any[] = [];

  walkJson(root, node => {
    if (
      node &&
      typeof node === "object" &&
      Object.prototype.hasOwnProperty.call(node, propName)
    ) {
      out.push((node as any)[propName]);
    }
  });

  return out;
}

function getStatebag(record: any): Record<string, any> | null {
  const statebag = record?.Content?.Statebag;
  return statebag && typeof statebag === "object" ? statebag : null;
}

function getStepMarker(
  statebag: Record<string, any> | null
): { step: string; timestamp: string } | null {
  const candidates = [statebag?.ORCH_CS, statebag?.ORCH_IDX];

  for (const candidate of candidates) {
    const value = candidate?.v;
    if (value === undefined || value === null) continue;

    const step = String(value).trim();
    if (!step || step === "0") continue;

    return {
      step,
      timestamp: String(candidate?.c ?? "")
    };
  }

  return null;
}

function getStatebagTimestamp(statebag: Record<string, any> | null): string {
  if (!statebag) return "";

  for (const key of Object.keys(statebag)) {
    if (key === "ComplexItems") continue;

    const timestamp = statebag[key]?.c;
    if (typeof timestamp === "string" && timestamp.trim()) {
      return timestamp;
    }
  }

  return "";
}

function normalizeIsoTimestamp(timestamp: string): string {
  return timestamp.replace(/\.(\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/, ".$1$2");
}

function millisecondsBetween(start: string, end: string): number | null {
  const startMs = Date.parse(normalizeIsoTimestamp(start));
  const endMs = Date.parse(normalizeIsoTimestamp(end));

  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return null;
  }

  return Math.max(0, endMs - startMs);
}

function addUniqueTechnicalProfile(
  profiles: TechnicalProfile[],
  profile: TechnicalProfile
): void {
  if (!profile.id) return;
  if (
    profiles.some(x => x.id === profile.id && x.protocol === profile.protocol)
  ) {
    return;
  }

  profiles.push(profile);
}

function collectTechnicalProfilesFromRecord(record: any): TechnicalProfile[] {
  return collectValuesEntriesByKey(record, "InitiatingClaimsExchange")
    .map(entry => ({
      id: String(entry?.Value?.TechnicalProfileId ?? ""),
      protocol: String(entry?.Value?.ProtocolProviderType ?? "")
    }))
    .filter(x => x.id);
}

function getClaimSnapshot(
  statebag: Record<string, any> | null
): Record<string, string> {
  const rawClaims = statebag?.["Complex-CLMS"] ?? statebag?.ComplexCLMS;
  const claims: Record<string, string> = {};

  if (!rawClaims || typeof rawClaims !== "object") {
    return claims;
  }

  for (const key of Object.keys(rawClaims)) {
    const rawValue = (rawClaims as Record<string, unknown>)[key];
    claims[key] =
      typeof rawValue === "string" ? rawValue : JSON.stringify(rawValue);
  }

  return claims;
}

function collectTraceTimestamps(traceJson: any[]): string[] {
  const timestamps: string[] = [];

  for (const rec of traceJson ?? []) {
    const timestamp = getStatebagTimestamp(getStatebag(rec));
    if (timestamp) {
      timestamps.push(timestamp);
    }
  }

  return timestamps;
}

function summarizeOrchestrationSteps(
  traceJson: any[]
): OrchestrationStepSummary[] {
  const summaries: OrchestrationStepSummary[] = [];
  const latestClaims: Record<string, string> = {};
  const timestamps = collectTraceTimestamps(traceJson);
  let current: OrchestrationStepSummary | null = null;

  for (const rec of traceJson ?? []) {
    const statebag = getStatebag(rec);
    const marker = getStepMarker(statebag);

    if (marker && (current === null || marker.step !== current.step)) {
      if (current) {
        current.endedAt = marker.timestamp || current.startedAt;
        current.durationMs = millisecondsBetween(
          current.startedAt,
          current.endedAt
        );
      }

      current = {
        step: marker.step,
        startedAt: marker.timestamp,
        endedAt: "",
        durationMs: null,
        technicalProfiles: [],
        claimsUpdated: []
      };
      summaries.push(current);
    }

    if (!current) continue;

    for (const profile of collectTechnicalProfilesFromRecord(rec)) {
      addUniqueTechnicalProfile(current.technicalProfiles, profile);
    }

    const claimSnapshot = getClaimSnapshot(statebag);
    for (const name of Object.keys(claimSnapshot)) {
      const value = claimSnapshot[name];
      if (latestClaims[name] === value) continue;

      current.claimsUpdated.push({
        name,
        previousValue: latestClaims[name] ?? "Null",
        value
      });
      latestClaims[name] = value;
    }
  }

  if (current) {
    current.endedAt = timestamps[timestamps.length - 1] ?? current.startedAt;
    current.durationMs = millisecondsBetween(
      current.startedAt,
      current.endedAt
    );
  }

  return summaries;
}

function extractTraceTimestamp(traceJson: any[]): string {
  for (const rec of traceJson ?? []) {
    const sb = rec?.Content?.Statebag;
    if (!sb || typeof sb !== "object") continue;

    for (const key of Object.keys(sb)) {
      if (key === "ComplexItems") continue;

      const entry = sb[key];
      const timestamp = entry?.c;

      if (typeof timestamp === "string" && timestamp.trim()) {
        return timestamp;
      }
    }
  }

  return "";
}

function parseManyJsonValues(text: string): any[] {
  const out: any[] = [];
  let i = 0;

  const skipWhitespace = () => {
    while (i < text.length && /\s/.test(text[i])) i++;
  };

  while (true) {
    skipWhitespace();
    if (i >= text.length) break;

    const start = i;
    const first = text[i];
    if (first !== "[" && first !== "{") {
      throw new Error(`Unexpected character '${first}' at position ${i}.`);
    }

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (; i < text.length; i++) {
      const c = text[i];

      if (inString) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (c === "\\") {
          escaped = true;
          continue;
        }
        if (c === '"') {
          inString = false;
        }
        continue;
      }

      if (c === '"') {
        inString = true;
        continue;
      }

      if (c === "[" || c === "{") depth++;
      if (c === "]" || c === "}") depth--;

      if (depth === 0) {
        i++;
        out.push(JSON.parse(text.slice(start, i)));
        break;
      }
    }

    if (depth !== 0) {
      throw new Error("Unterminated JSON value.");
    }
  }

  return out;
}

function parseInput(raw: string): any[][] {
  const traces = parseManyJsonValues(raw);
  if (!traces.length) throw new Error("No JSON found.");

  return Array.isArray(traces[0]) ? traces : [traces];
}

function computeOrchestrationSteps(traceJson: any[]): string {
  const steps: string[] = [];
  const seen = new Set<string>();

  for (const rec of traceJson ?? []) {
    const marker = getStepMarker(getStatebag(rec));
    if (!marker || seen.has(marker.step)) continue;

    seen.add(marker.step);
    steps.push(marker.step);
  }

  return steps.join(", ");
}

function analyzeTrace(traceJson: any[]): TraceAnalysis {
  const header = traceJson.find(x => x?.Kind === "Headers")?.Content ?? {};
  const technicalProfiles = collectValuesEntriesByKey(
    traceJson,
    "InitiatingClaimsExchange"
  )
    .map(entry => ({
      id: entry?.Value?.TechnicalProfileId ?? "",
      protocol: entry?.Value?.ProtocolProviderType ?? ""
    }))
    .filter(x => x.id);

  const validationTechnicalProfiles = collectValuesEntriesByKey(
    traceJson,
    "TechnicalProfileId"
  )
    .map(entry => String(entry?.Value ?? ""))
    .filter(Boolean);

  const claimsTransformations = collectValuesEntriesByKey(
    traceJson,
    "ClaimsTransformation"
  )
    .map(entry => {
      const values = entry?.Value?.Values ?? [];
      const group: ClaimsTransformationGroup = { id: "", rows: [] };

      for (const obj of values) {
        if (obj?.Key === "Id") {
          group.id = String(obj?.Value ?? "");
        } else {
          const item = String(obj?.Key ?? "").replace("Result", "OutputClaim");
          const name =
            obj?.Key === "InputClaim" || obj?.Key === "Result"
              ? String(obj?.Value?.PolicyClaimType ?? "")
              : String(obj?.Value?.Id ?? "");

          group.rows.push({
            item,
            name,
            value: String(obj?.Value?.Value ?? "")
          });
        }
      }

      return group;
    })
    .filter(group => group.id || group.rows.length > 0);

  const exceptions = collectExceptions(traceJson);

  const tokens = collectObjectsWithProp(traceJson, "ContentType").map(obj => {
    const contentType = String(obj?.ContentType ?? "");
    const rawValue = String(obj?.Value ?? "");
    const lowerContentType = contentType.toLowerCase();

    const parts = rawValue.split(";");
    const candidateValue = parts[0] ?? rawValue;
    const label = (parts[2] ?? "").trim();

    let actualValue = rawValue;
    let kind: TokenInfo["kind"] = "text";
    let jwtMsLink: string | undefined;

    if (
      (lowerContentType === "jwt" || lowerContentType === "unspecified") &&
      looksLikeJwt(candidateValue)
    ) {
      actualValue = candidateValue;
      kind = "jwt";
      jwtMsLink = `https://jwt.ms/#id_token=${encodeURIComponent(
        candidateValue
      )}`;
    } else if (lowerContentType === "json" || looksLikeJson(rawValue)) {
      kind = "json";
    } else if (looksLikeFormEncoded(rawValue)) {
      kind = "form";
    }

    return {
      label: label || "Token payload",
      contentType,
      value: actualValue,
      kind,
      preview: buildPreview(actualValue),
      jwtMsLink
    };
  });

  const normalizedJson = JSON.parse(
    JSON.stringify(traceJson).replaceAll("Complex-CLMS", "ComplexCLMS")
  );

  const claimObjects = collectByPropName(normalizedJson, "ComplexCLMS");
  const claims: Record<string, string[]> = {};

  for (const obj of claimObjects) {
    if (!obj || typeof obj !== "object") continue;

    for (const key of Object.keys(obj)) {
      const rawValue = (obj as Record<string, unknown>)[key];
      const value =
        typeof rawValue === "string" ? rawValue : JSON.stringify(rawValue);
      if (!claims[key]) {
        claims[key] = [value];
      } else if (claims[key][claims[key].length - 1] !== value) {
        claims[key].push(value);
      }
    }
  }

  const analyzedClaims = analyzeClaims(claims);

  return {
    policyId: header.PolicyId ?? "",
    correlationId: header.CorrelationId ?? "",
    eventInstance: header.EventInstance ?? "Trace",
    orchestrationSteps: computeOrchestrationSteps(traceJson),
    orchestrationStepSummaries: summarizeOrchestrationSteps(traceJson),
    journeyCompleted:
      collectValuesEntriesByKey(traceJson, "JourneyCompleted").length > 0,
    technicalProfiles,
    validationTechnicalProfiles,
    claimsTransformations,
    exceptions,
    tokens,
    claims: analyzedClaims,
    rawJson: JSON.stringify(traceJson, null, 2),
    timestamp: extractTraceTimestamp(traceJson)
  };
}

export function analyzeInput(raw: string): TraceAnalysis[] {
  return parseInput(raw).map(analyzeTrace);
}
